/**
 * Fetch wrapper that handles 401 by refreshing the access token and retrying once.
 * Used for raw fetch() calls (e.g. streaming endpoints) that bypass the axios interceptor.
 */
import { config } from '../config';

async function refreshAccessToken(): Promise<string> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await fetch(`${config.apiBaseUrl}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) {
    throw new Error('Token refresh failed');
  }

  const data = await response.json();
  localStorage.setItem('access_token', data.access_token);
  localStorage.setItem('refresh_token', data.refresh_token);
  return data.access_token;
}

/**
 * Fetch with automatic token refresh on 401.
 * If the initial request returns 401, refreshes the token and retries once.
 * On refresh failure, clears tokens and redirects to login.
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit
): Promise<Response> {
  const response = await fetch(url, options);

  if (response.status !== 401) {
    return response;
  }

  // Got 401 — try refreshing the token
  try {
    const newToken = await refreshAccessToken();

    // Retry with new token
    const retryHeaders = new Headers(options.headers);
    retryHeaders.set('Authorization', `Bearer ${newToken}`);

    return await fetch(url, { ...options, headers: retryHeaders });
  } catch {
    // Refresh failed — clear auth and redirect
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/login';
    throw new Error('Session expired. Please log in again.');
  }
}
